import {FC, useState} from 'react'
import {useNotify, useTranslate} from 'react-admin'
import {makeStyles} from 'tss-react/mui'

import {
  ChatMessageTemplateButton,
  ChatMessageTemplateButtonTypesEnum,
  WhatsappMessages,
} from '../../types/graphqlSchema'
import {CopyIcon, ExternalLinkIcon, ShareIcon, WhatsappAudioCallIcon} from '../icons'
import LineClamp from '../LineClamp'

const ChatMessageTemplateButtonList: FC<ChatMessageTemplateButtonListProps> = ({
  chatMessageTemplateButtons,
  whatsappMessage,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const {classes: styles} = useStyles({})
  const [showAllButtons, setShowAllButtons] = useState(false)
  const copyCodeToClipBoard = () => {
    if (!whatsappMessage?.templateButtonCouponCode) return
    navigator.clipboard.writeText(whatsappMessage.templateButtonCouponCode)
    notify(
      'chatMessageTemplateButtonList.copyCodeCopiedToClipboard',
      {messageArgs: {code: whatsappMessage.templateButtonCouponCode}},
    )
  }
  return (
    <div>
      {chatMessageTemplateButtons
        ?.filter(
          (_, i) => showAllButtons || chatMessageTemplateButtons.length <= 3 || i < 2
        ).map((button, i) => {
          // Count how many dynamic buttons came before this one
          const dynamicButtonIndex = chatMessageTemplateButtons.slice(0, i)
            .filter(b => b.urlType === 'DYNAMIC').length
          return (
            <div key={i}>
              {button.type === ChatMessageTemplateButtonTypesEnum.QuickReply && (
                <div className={styles.button}>
                  <span className={styles.viewerButtonText}>
                    <ShareIcon size="small" />
                    <LineClamp clamp={1}>{button.quickReply}</LineClamp>
                  </span>
                </div>
              )}
              {button.type === ChatMessageTemplateButtonTypesEnum.CopyCode && (
                <div onClick={copyCodeToClipBoard} className={styles.button}>
                  <span className={styles.viewerButtonText}>
                    <CopyIcon size="small" />
                    <LineClamp clamp={1}>Copy offer code</LineClamp>
                  </span>
                </div>
              )}
              {button.type === ChatMessageTemplateButtonTypesEnum.Url && (
                <div className={styles.button}>
                  <a
                    target="_blank"
                    href={
                      button.urlType === 'DYNAMIC' ?
                        button.url?.replace(
                          '{{1}}',
                          whatsappMessage
                            ?.templateButtonUrlParameters?.[dynamicButtonIndex] as string
                        ) :
                        button.url
                    }
                    className={styles.viewerButtonText}
                    style={{textDecoration: 'none'}}
                  >
                    <ExternalLinkIcon size="small" />
                    <LineClamp clamp={1}>{button.urlText}</LineClamp>
                  </a>
                </div>
              )}
              {button.type === ChatMessageTemplateButtonTypesEnum.PhoneNumber && (
                <div className={styles.button}>
                  <a
                    target="_blank"
                    href={`tel:${button.phoneNumberCountryCode}${button.phoneNumber}`}
                    className={styles.viewerButtonText}
                    style={{textDecoration: 'none'}}
                  >
                    <WhatsappAudioCallIcon size="small" />
                    <LineClamp clamp={1}>{button.phoneNumberText}</LineClamp>
                  </a>
                </div>
              )}
            </div>
          )
        })}
      {chatMessageTemplateButtons && (chatMessageTemplateButtons?.length > 3) && (
        <div
          style={{cursor: 'pointer'}}
          className={styles.button}
          onClick={() => setShowAllButtons(v => !v)}
        >
          {showAllButtons ?
            translate(
              'chatMessageTemplateEditor.chatMessageTemplateField.hideOptions'
            ) :
            translate(
              'chatMessageTemplateEditor.chatMessageTemplateField.seeAllOptions'
            )
          }
        </div>
      )}
    </div>
  )
}

const useStyles = makeStyles<any>()(theme => ({
  button: {
    '& .MuiFormHelperText-root': {
      textAlign: 'center',
    },
    '& .MuiTextField-root': {
      '& .MuiFormHelperText-root': {
        margin: 0,
      },
      '& .MuiFormHelperText-root :not(Mui-error)': {
        display: 'none',
      },
      margin: 0,
      padding: 0,
      width: '60% !important',
    },
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.disabled.main}`,
    cursor: 'pointer',
    display: 'flex',
    fill: '#34B7F1',
    justifyContent: 'center',
    paddingBottom: theme.remSpacing(0.5),
    paddingTop: theme.remSpacing(0.5),
  },
  viewerButtonText: {
    alignItems: 'center',
    color: '#34B7F1',
    display: 'flex',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    gap: theme.remSpacing(1),
    height: theme.typography.pxToRem(40),
    lineHeight: theme.typography.pxToRem(40),
    padding: 0,
    textAlign: 'center',
    verticalAlign: 'middle',
    wordBreak: 'break-all',
  },
}))

interface ChatMessageTemplateButtonListProps {
  chatMessageTemplateButtons: ChatMessageTemplateButton[] | undefined
  whatsappMessage?: WhatsappMessages
}

export default ChatMessageTemplateButtonList
